import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { extractErr } from '../../api/error-message';
import { DataTable, type Column } from '../../components/data-table';
import { Modal } from '../../components/modal';
import { useDebouncedValue } from '../../components/table-search';
import { SplitBar, dateTime, defaultRange, inr2, todayIst, type Split } from './shared';

interface SplitLine {
  id: string;
  title: string;
  variant: string | null;
  seller: string;
  qty: number;
  customerPaid: number;
  tax: number;
  sellerShare: number;
  commission: number;
  platformFee: number;
  priceMarkup: number;
  companyShare: number;
}

interface OrderSplit extends Split {
  id: string;
  orderNumber: string;
  paymentMethod: string;
  deliveredAt: string;
  customer: { name: string | null; phone: string };
  deliveredBy: string[];
  lines: SplitLine[];
}

const PER_PAGE = 15;
const money = (n: number) => <span className="font-tabular">{inr2(n)}</span>;

export function OrderSplitTab() {
  const [range, setRange] = useState(() => defaultRange('day'));
  const [rows, setRows] = useState<OrderSplit[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const q = useDebouncedValue(search.trim(), 300);
  const [selected, setSelected] = useState<OrderSplit | null>(null);

  useEffect(() => { setPage(1); }, [q, range]);

  useEffect(() => {
    if (range.from > range.to) return;
    let alive = true;
    api
      .get<{ items: OrderSplit[]; total: number }>('/admin/analytics/order-splits', {
        params: { ...range, ...(q ? { q } : {}), page, limit: PER_PAGE },
      })
      .then(({ data }) => { if (alive) { setRows(data.items); setTotal(data.total); } })
      .catch((err) => { if (alive) toast.error(extractErr(err)); })
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [range, q, page]);

  const columns: Column<OrderSplit>[] = [
    {
      key: 'orderNumber', label: 'Order', primary: true,
      render: (o) => (
        <div>
          <p className="font-semibold text-ink-900">{o.orderNumber}</p>
          <p className="text-[12.5px] text-ink-500">{dateTime(o.deliveredAt)}</p>
        </div>
      ),
    },
    {
      key: 'customer', label: 'Customer',
      render: (o) => (
        <div>
          <p className="text-ink-900">{o.customer.name || '—'}</p>
          <p className="text-[12.5px] text-ink-500">{o.customer.phone}</p>
        </div>
      ),
    },
    { key: 'customerPaid', label: 'Customer paid', align: 'right', render: (o) => money(o.customerPaid) },
    { key: 'sellerShare', label: 'Seller', align: 'right', render: (o) => money(o.sellerShare) },
    { key: 'partnerShare', label: 'Delivery partner', align: 'right', render: (o) => money(o.partnerShare) },
    { key: 'tax', label: 'GST', align: 'right', render: (o) => money(o.tax) },
    {
      key: 'companyRevenue', label: 'Company', align: 'right',
      render: (o) => <span className="font-tabular font-semibold text-ink-900">{inr2(o.companyRevenue)}</span>,
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-[13px] text-ink-500">
          Delivered from
          <input
            type="date" className="input mt-1 block" value={range.from} max={range.to}
            onChange={(e) => e.target.value && setRange((x) => ({ ...x, from: e.target.value }))}
          />
        </label>
        <label className="text-[13px] text-ink-500">
          To
          <input
            type="date" className="input mt-1 block" value={range.to} min={range.from} max={todayIst()}
            onChange={(e) => e.target.value && setRange((x) => ({ ...x, to: e.target.value }))}
          />
        </label>
        <p className="pb-2 text-[13px] text-ink-500">Click an order to see each product's split.</p>
      </div>

      <DataTable
        columns={columns}
        data={rows}
        rowKey={(o) => o.id}
        loading={!loaded}
        perPage={PER_PAGE}
        onRowClick={setSelected}
        searchPlaceholder="Search order no., customer name or phone…"
        searchValue={search}
        onSearchChange={setSearch}
        serverPagination={{ page, totalItems: total, onPageChange: setPage }}
        exportName={`order-splits-${range.from}-to-${range.to}`}
      />

      {selected && <OrderSplitModal order={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function OrderSplitModal({ order: o, onClose }: { order: OrderSplit; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const b = o.companyBreakdown;
  return (
    <Modal
      title={`Order ${o.orderNumber}`}
      description={
        <>
          {o.customer.name || o.customer.phone} · {o.paymentMethod === 'ONLINE' ? 'Paid online' : o.paymentMethod} · delivered {dateTime(o.deliveredAt)}
          {o.deliveredBy.length > 0 && <> · by {o.deliveredBy.join(', ')}</>}
        </>
      }
      size="lg"
      onClose={onClose}
      footer={<button type="button" className="btn btn-tonal" onClick={onClose}>Close</button>}
    >
      <div className="space-y-6">
        <div>
          <p className="mb-2 text-[13.5px] text-ink-500">
            Customer paid <span className="font-tabular text-[18px] font-semibold text-ink-900">{inr2(o.customerPaid)}</span>
          </p>
          <SplitBar split={o} />
        </div>

        <div>
          <h4 className="mb-2 text-[14px] font-semibold text-ink-900">Per product</h4>
          <div className="overflow-x-auto rounded-[12px] border border-ink-200">
            <table className="w-full min-w-[640px] text-[13.5px]">
              <thead className="bg-surface-sunk text-left text-ink-500">
                <tr>
                  <th className="px-3 py-2 font-medium">Product</th>
                  <th className="px-3 py-2 text-right font-medium">Customer paid</th>
                  <th className="px-3 py-2 text-right font-medium">Seller</th>
                  <th className="px-3 py-2 text-right font-medium">GST</th>
                  <th className="px-3 py-2 text-right font-medium">Company</th>
                </tr>
              </thead>
              <tbody>
                {o.lines.map((l) => (
                  <tr key={l.id} className="border-t border-ink-200 align-top">
                    <td className="px-3 py-2">
                      <p className="text-ink-900">{l.title} <span className="text-ink-500">× {l.qty}</span></p>
                      <p className="text-[12.5px] text-ink-500">{[l.variant, l.seller].filter(Boolean).join(' · ')}</p>
                    </td>
                    <td className="px-3 py-2 text-right">{money(l.customerPaid)}</td>
                    <td className="px-3 py-2 text-right">{money(l.sellerShare)}</td>
                    <td className="px-3 py-2 text-right">{money(l.tax)}</td>
                    <td className="px-3 py-2 text-right">
                      <span className="font-tabular font-semibold text-ink-900">{inr2(l.companyShare)}</span>
                      <p className="text-[12px] text-ink-500">
                        commission {inr2(l.commission)}
                        {l.platformFee > 0 && <> · fee {inr2(l.platformFee)}</>}
                        {l.priceMarkup > 0 && <> · markup {inr2(l.priceMarkup)}</>}
                      </p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <h4 className="mb-2 text-[14px] font-semibold text-ink-900">Company revenue on this order</h4>
          <dl className="space-y-1.5 text-[14px] text-ink-700">
            <Row label="From products (commission, fees, markup)" value={b.commissionAndFees + b.priceMarkup} />
            <Row label="Delivery charge paid by customer" value={b.deliveryCharges} />
            {b.discounts > 0 && <Row label="Discount given" value={-b.discounts} />}
            <Row label="Paid to delivery partner" value={-b.partnerPayouts} />
            <div className="flex justify-between border-t border-ink-200 pt-2 font-semibold text-ink-900">
              <dt>Company revenue</dt>
              <dd className="font-tabular">{inr2(o.companyRevenue)}</dd>
            </div>
          </dl>
        </div>
      </div>
    </Modal>
  );
}

function Row({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex justify-between gap-4">
      <dt>{label}</dt>
      <dd className="font-tabular">{value < 0 ? `− ${inr2(-value)}` : inr2(value)}</dd>
    </div>
  );
}
